import type { Score } from './types';
import { SCORE_VERSION } from './types';

export type ValidationResult = { ok: true; score: Score } | { ok: false; error: string };

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const DUR_BASES = new Set([1, 2, 4, 8, 16, 32, 64]);
const STEPS = new Set(['C', 'D', 'E', 'F', 'G', 'A', 'B']);

/** Limits that keep a single document bounded in size. */
export const LIMITS = { bars: 2000, tracks: 32, voices: 4, beatsPerVoice: 256, notesPerBeat: 16 };

/**
 * Structural validation of an untrusted score document (API payloads, imports).
 * Checks shapes and ranges, not musical sense.
 */
export function validateScore(x: unknown): ValidationResult {
  const fail = (error: string): ValidationResult => ({ ok: false, error });
  if (!isObj(x)) return fail('score must be an object');
  if (x.v !== SCORE_VERSION) return fail(`unsupported score version ${String(x.v)}`);
  if (!isObj(x.meta) || typeof x.meta.title !== 'string') return fail('meta.title must be a string');
  if (!Array.isArray(x.bars) || x.bars.length === 0) return fail('bars must be a non-empty array');
  if (x.bars.length > LIMITS.bars) return fail(`too many bars (max ${LIMITS.bars})`);
  if (!Array.isArray(x.tempos)) return fail('tempos must be an array');
  if (!Array.isArray(x.tracks) || x.tracks.length === 0) return fail('tracks must be a non-empty array');
  if (x.tracks.length > LIMITS.tracks) return fail(`too many tracks (max ${LIMITS.tracks})`);
  for (const [i, b] of x.bars.entries()) {
    if (!isObj(b)) return fail(`bars[${i}] must be an object`);
    if (b.timeSig !== undefined) {
      const ts = b.timeSig as Record<string, unknown>;
      if (!isObj(ts) || !Number.isInteger(ts.num) || (ts.num as number) < 1 || (ts.num as number) > 64 || !DUR_BASES.has(ts.den as number))
        return fail(`bars[${i}].timeSig invalid`);
    }
    if (b.keySig !== undefined) {
      const ks = b.keySig as Record<string, unknown>;
      if (!isObj(ks) || !Number.isInteger(ks.fifths) || Math.abs(ks.fifths as number) > 7) return fail(`bars[${i}].keySig invalid`);
    }
  }
  if (!isObj(x.bars[0]) || !(x.bars[0] as Record<string, unknown>).timeSig) return fail('bars[0] must carry a time signature');
  for (const [i, t] of (x.tempos as unknown[]).entries()) {
    if (!isObj(t) || typeof t.bpm !== 'number' || t.bpm < 5 || t.bpm > 1000 || !Number.isInteger(t.bar)) return fail(`tempos[${i}] invalid`);
  }
  for (const [ti, t] of (x.tracks as unknown[]).entries()) {
    if (!isObj(t)) return fail(`tracks[${ti}] must be an object`);
    if (typeof t.id !== 'string' || typeof t.name !== 'string') return fail(`tracks[${ti}] needs id and name`);
    if (!Array.isArray(t.bars) || t.bars.length !== x.bars.length) return fail(`tracks[${ti}].bars must parallel score bars`);
    if (t.tuning !== undefined && (!Array.isArray(t.tuning) || t.tuning.length > 12 || t.tuning.some((n) => !Number.isInteger(n) || n < 0 || n > 127)))
      return fail(`tracks[${ti}].tuning invalid`);
    for (const [bi, tb] of t.bars.entries()) {
      if (!isObj(tb) || !Array.isArray(tb.voices) || tb.voices.length === 0 || tb.voices.length > LIMITS.voices)
        return fail(`tracks[${ti}].bars[${bi}].voices invalid`);
      for (const [vi, v] of tb.voices.entries()) {
        if (!Array.isArray(v) || v.length > LIMITS.beatsPerVoice) return fail(`tracks[${ti}].bars[${bi}].voices[${vi}] invalid`);
        for (const [i, b] of v.entries()) {
          const where = `tracks[${ti}].bars[${bi}].voices[${vi}][${i}]`;
          if (!isObj(b) || typeof b.id !== 'string') return fail(`${where} invalid`);
          const d = b.duration as Record<string, unknown>;
          if (!isObj(d) || !DUR_BASES.has(d.base as number) || ![0, 1, 2].includes(d.dots as number)) return fail(`${where}.duration invalid`);
          if (!Array.isArray(b.notes) || b.notes.length > LIMITS.notesPerBeat) return fail(`${where}.notes invalid`);
          for (const n of b.notes as unknown[]) {
            if (!isObj(n) || typeof n.id !== 'string' || !isObj(n.pitch)) return fail(`${where} note invalid`);
            const p = n.pitch;
            if (!STEPS.has(p.step as string) || !Number.isInteger(p.alter) || Math.abs(p.alter as number) > 2 || !Number.isInteger(p.octave) || (p.octave as number) < -1 || (p.octave as number) > 9)
              return fail(`${where} pitch invalid`);
          }
        }
      }
    }
  }
  return { ok: true, score: x as unknown as Score };
}
